import React, { useState } from 'react';
import PublicLayout from '../components/public/PublicLayout';
import SEO from '../components/common/SEO';
import { seoConfig } from '../config/seoConfig';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    inquiryType: 'general',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await axios.post(`${API_URL}/communication/contact/submit`, formData);

      if (response.data.success) {
        toast.success('Thank you for contacting ARCA! We will get back to you soon. Check your email for confirmation.');
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          inquiryType: 'general',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error(error.response?.data?.message || 'Failed to submit contact form. Please try again.');
    }
  };

  return (
    <PublicLayout>
      <SEO
        title={seoConfig.contact.title}
        description={seoConfig.contact.description}
        keywords={seoConfig.contact.keywords}
        url={seoConfig.contact.url}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          'name': 'Contact ARCA',
          'description': 'Contact Africa Respiratory Care Association',
          'mainEntity': {
            '@type': 'Organization',
            'name': 'Africa Respiratory Care Association',
            'contactPoint': [
              {
                '@type': 'ContactPoint',
                'telephone': '+233(0)24 213 0397',
                'contactType': 'Customer Service',
                'areaServed': 'Africa',
                'availableLanguage': 'English'
              },
              {
                '@type': 'ContactPoint',
                'telephone': '+1(952)228-1482',
                'contactType': 'International Office',
                'areaServed': 'US',
                'availableLanguage': 'English'
              }
            ],
            'email': 'info@africaresp.org',
            'address': [
              {
                '@type': 'PostalAddress',
                'addressCountry': 'GH',
                'addressLocality': 'Accra'
              },
              {
                '@type': 'PostalAddress',
                'addressCountry': 'US',
                'addressRegion': 'GA'
              }
            ]
          }
        }}
      />
      <div className="py-5" style={{ background: 'linear-gradient(135deg, rgba(107, 142, 35, 0.05) 0%, #ffffff 100%)' }}>
        <div className="container">
          {/* Header */}
          <div className="row justify-content-center text-center mb-5">
            <div className="col-lg-8">
              <h1 className="display-4 fw-bold text-dark mb-4">Contact Us</h1>
              <p className="lead text-muted">
                Get in touch with ARCA - We would love to hear from you
              </p>
            </div>
          </div>

          <div className="row g-5">
            {/* Contact Information */}
            <div className="col-lg-4">
              <div className="row g-4">
                {/* Ghana Office */}
                <div className="col-12">
                  <div className="card shadow-sm border-0 h-100">
                    <div className="card-body p-4">
                      <h4 className="fw-bold text-dark mb-3">Ghana Office</h4>
                      <div className="d-flex align-items-start mb-3">
                        <MapPin className="me-3 flex-shrink-0 mt-1" size={20} style={{ color: '#6B8E23' }} />
                        <div>
                          <p className="mb-1 text-dark">13 MANSTE AFOTEY ADZIN II RD,<br />GT-373-9745, Tema Metropolis,<br />Greater Accra Region, Ghana</p>
                          <small className="text-muted">Headquarters</small>
                        </div>
                      </div>
                      <div className="d-flex align-items-start mb-3">
                        <Phone className="me-3 flex-shrink-0 mt-1" size={20} style={{ color: '#6B8E23' }} />
                        <p className="mb-0 text-dark">+233(0)24 213 0397</p>
                      </div>
                      <div className="d-flex align-items-start">
                        <Mail className="me-3 flex-shrink-0 mt-1" size={20} style={{ color: '#6B8E23' }} />
                        <p className="mb-0 text-dark">info@africaresp.org</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* USA Office */}
                <div className="col-12">
                  <div className="card shadow-sm border-0 h-100">
                    <div className="card-body p-4">
                      <h4 className="fw-bold text-dark mb-3">USA Office</h4>
                      <div className="d-flex align-items-start mb-3">
                        <MapPin className="me-3 flex-shrink-0 mt-1" size={20} style={{ color: '#6B8E23' }} />
                        <div>
                          <p className="mb-1 text-dark">235 Peachtree Street NE Suite 400,<br />Atlanta, GA, 30303</p>
                          <small className="text-muted">International Office</small>
                        </div>
                      </div>
                      <div className="d-flex align-items-start mb-3">
                        <Phone className="me-3 flex-shrink-0 mt-1" size={20} style={{ color: '#6B8E23' }} />
                        <p className="mb-0 text-dark">+1(952)228-1482</p>
                      </div>
                      <div className="d-flex align-items-start">
                        <Mail className="me-3 flex-shrink-0 mt-1" size={20} style={{ color: '#6B8E23' }} />
                        <p className="mb-0 text-dark">info@africaresp.org</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Office Hours */}
                <div className="col-12">
                  <div className="card text-white arca-gradient shadow-sm border-0">
                    <div className="card-body p-4">
                      <div className="d-flex align-items-center mb-3">
                        <Clock className="me-3" size={24} />
                        <h4 className="fw-bold mb-0">Office Hours</h4>
                      </div>
                      <div>
                        <p className="mb-2"><strong>Monday - Friday:</strong> 9:00 AM - 4:30 PM (GMT)</p>
                        <p className="mb-2"><strong>Saturday:</strong> Closed</p>
                        <p className="mb-0"><strong>Sunday:</strong> Closed</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="col-lg-8">
              <div className="card shadow-sm border-0">
                <div className="card-body p-4 p-lg-5">
                  <h2 className="h3 fw-bold text-dark mb-4">Send Us a Message</h2>
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3 mb-3">
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className="form-control"
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="form-control"
                          required
                        />
                      </div>
                    </div>

                    <div className="row g-3 mb-3">
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Phone Number</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="form-control"
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Inquiry Type *</label>
                        <select
                          name="inquiryType"
                          value={formData.inquiryType}
                          onChange={handleChange}
                          className="form-select"
                          required
                        >
                          <option value="general">General Inquiry</option>
                          <option value="volunteer">Volunteer Opportunity</option>
                          <option value="partnership">Partnership</option>
                          <option value="media">Media Inquiry</option>
                          <option value="donation">Donation</option>
                          <option value="membership">Membership</option>
                        </select>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-semibold">Subject *</label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="form-control"
                        required
                      />
                    </div>

                    <div className="mb-4">
                      <label className="form-label fw-semibold">Message *</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        rows="6"
                        className="form-control"
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary btn-lg w-100 fw-bold"
                      style={{ transition: 'all 0.3s ease' }}
                    >
                      Send Message
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default ContactPage;